/*
  ils-search-logger.js — TASK-2816 (Trello ILS-026); TASK-2902 fixes
  ------------------------------------------------------------------------
  Anonymous, consent-FREE, fail-silent storefront search interceptor.

  TASK-2902: the /search results page parse was using stock-Dawn selectors
  (#product-grid .grid__item / .card__heading) that match NOTHING in the live
  custom-search section (<ul id="product-grid" class="ils-pcl-grid"><li
  class="ils-pcl-grid__item"> … <a class="ils-pcl__name">), so the page path
  always logged results_count=0. Now it reads the real custom-search selectors
  (Dawn kept as fallback), reads the TRUE total from data-total-results on
  #product-grid (added in custom-search.liquid) instead of the one-page item
  count, and tags each event with source ('dropdown' | 'page') so the backend
  can stop a page-0 race from clobbering a good dropdown count.

  Feeds the ils-admin backend /api/search-logs/* (alert-dedication
  PRODUCTION — the SAME backend host ils-tracker.js posts /api/track to).

  This is a SEPARATE system from ils-tracker.js:
    - NOT gated on GDPR consent / localStorage.ils_consent.
    - No personal data — only the search term + which results were shown +
      which one was clicked. The anonymous session id lives in sessionStorage
      (tab-scoped, dies with the visit, never a cookie, never localStorage),
      so it is not persistent tracking and does not require consent.

  It does NOT touch the search UI/markup at all. It only:
    1. Watches the native Shopify predictive-search fetch (the theme's
       predictive-search.js issues `?...&section_id=predictive-search`),
       reads the query from the URL and the results from the response HTML,
       and (debounced) POSTs /api/search-logs/query.
    2. On the full /search results page, POSTs one /query for the page.
    3. Delegates clicks on result links (dropdown OR results page) and POSTs
       /api/search-logs/click with the clicked rank + target.

  Everything is wrapped in try/catch + fire-and-forget (sendBeacon, fallback
  fetch keepalive). A network error, a missing backend, or a blocked
  sessionStorage can NEVER break the real search.

  Works in all 6 locales (ru / de / en / lv / et / lt) — locale is read from
  window.ILS_LANG (request.locale.iso_code, already exposed in theme.liquid
  TASK-1761); no new Liquid data block is needed.
*/
(function () {
  'use strict';

  // TASK-2820 go-live: production backend (search-logs endpoints merged to prod
  // v1.0.97). Flipped from staging together with porting the interceptor to the
  // live MAIN theme (199402717525) — same staging->production flip ils-tracker.js
  // did in TASK-2796.
  var API_BASE = 'https://alert-dedication-production.up.railway.app';
  var QUERY_ENDPOINT = API_BASE + '/api/search-logs/query';
  var CLICK_ENDPOINT = API_BASE + '/api/search-logs/click';

  var DEBOUNCE_MS = 600;          // pause after typing before we log the query
  var SESSION_KEY = 'ils_search_sid';
  var MAX_RESULTS = 50;           // backend caps at 50 anyway

  // ---- locale (full Shopify Markets locale, e.g. de-de / ru-eu) ----
  // TASK-2821: the stored locale must be the REAL market locale
  // (domain/{lang}-{country}), NOT the bare language code from window.ILS_LANG.
  // The 10 markets are de-de / ru-de / lv-lv / ru-lv / lt-lt / ru-lt / et-ee /
  // ru-ee / en-eu / ru-eu, and the market slug is always the first path segment
  // on ilovesnacks.eu (/de-de/…, /ru-eu/…). Read it from the URL; fall back to
  // combining the language + Shopify country, then to the raw language code.
  var LOCALE = '';
  try {
    var KNOWN_LOCALES = ['de-de','ru-de','lv-lv','ru-lv','lt-lt','ru-lt','et-ee','ru-ee','en-eu','ru-eu'];
    var seg = String((location && location.pathname) || '').split('/')[1].toLowerCase();
    if (KNOWN_LOCALES.indexOf(seg) !== -1 || /^[a-z]{2}-[a-z]{2}$/.test(seg)) {
      LOCALE = seg;                                   // canonical market slug from the URL
    } else {
      var lang = String(window.ILS_LANG ||
                        (window.ILS_PAGE && window.ILS_PAGE.lang) ||
                        (window.Shopify && window.Shopify.locale) || '')
                 .toLowerCase().split('-')[0];
      var country = '';
      try { country = String((window.Shopify && window.Shopify.country) || '').toLowerCase(); } catch (e2) { country = ''; }
      if (lang === 'en') LOCALE = 'en-eu';            // English is the intl market only
      else if (lang) LOCALE = country ? (lang + '-' + country) : lang;
    }
    LOCALE = String(LOCALE || '').trim();
  } catch (e) { LOCALE = ''; }

  // ---- anonymous, tab-scoped session id ----
  function genId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return 'sl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  var SID = '';
  try {
    SID = window.sessionStorage.getItem(SESSION_KEY) || '';
    if (!SID) { SID = genId(); window.sessionStorage.setItem(SESSION_KEY, SID); }
  } catch (e) {
    // sessionStorage blocked (private mode / cookie wall) → ephemeral per-load id.
    SID = genId();
  }

  // ---- best-effort query language signal (only the cheap, useful one) ----
  function guessQueryLanguage(q) {
    try { if (/[Ѐ-ӿ]/.test(q)) return 'ru'; } catch (e) {}
    return undefined; // Latin scripts are ambiguous (de/en/lv/lt/et) → let backend skip
  }

  // ---- fire-and-forget POST (never blocks, never throws) ----
  // TASK-2820: use fetch({keepalive:true}) as the PRIMARY transport, NOT
  // navigator.sendBeacon. A cross-origin sendBeacon with an application/json
  // Blob triggers a CORS preflight it cannot satisfy and fails at the network
  // layer (net::ERR_FAILED) while still returning true — so the old code
  // silently dropped every event and never fell back. keepalive:true is
  // navigation-safe (survives the click->navigate on result links) and passes
  // CORS against the backend (verified). sendBeacon(text/plain) is kept only as
  // a last-ditch fallback for the rare no-fetch browser (best-effort).
  function post(url, payload) {
    try {
      var body = JSON.stringify(payload);
      if (typeof fetch === 'function') {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          mode: 'cors',
          credentials: 'omit',
          keepalive: true
        }).catch(function () {});
        return;
      }
      if (navigator.sendBeacon) {
        try { navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' })); } catch (e) {}
      }
    } catch (e) {}
  }

  // ---- helpers ----
  function qParam(url) {
    try {
      var m = String(url).match(/[?&]q=([^&]*)/);
      if (!m) return '';
      return decodeURIComponent(m[1].replace(/\+/g, ' ')).trim();
    } catch (e) { return ''; }
  }

  function handleFromHref(href) {
    try {
      var m = String(href || '').match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function textOf(el) {
    return el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  // Parse the predictive-search product results out of a parsed document.
  function parsePredictiveResults(doc) {
    var out = [];
    try {
      var lis = doc.querySelectorAll('#predictive-search-results-products-list > li');
      for (var i = 0; i < lis.length && out.length < MAX_RESULTS; i++) {
        var li = lis[i];
        var a = li.querySelector('a[href]');
        var heading = li.querySelector('.predictive-search__item-heading');
        var item = {
          position: i + 1,
          handle: handleFromHref(a && a.getAttribute('href')),
          title: textOf(heading)
        };
        if (item.handle || item.title) out.push(item);
      }
    } catch (e) {}
    return out;
  }

  function predictiveTotalCount(doc) {
    try {
      var el = doc.querySelector('[data-total-results]');
      if (el) {
        var n = parseInt(el.getAttribute('data-total-results'), 10);
        if (isFinite(n)) return n;
      }
    } catch (e) {}
    return undefined;
  }

  // ===================================================================
  // 1) Predictive search (dropdown) — wrap fetch, debounce the /query POST
  // ===================================================================
  var pending = null;   // { query, results, count }
  var debTimer = null;

  function schedulePredictiveLog(query, results, count) {
    pending = { query: query, results: results, count: count };
    if (debTimer) clearTimeout(debTimer);
    debTimer = setTimeout(function () {
      var p = pending;
      pending = null;
      debTimer = null;
      if (!p || !p.query) return;
      post(QUERY_ENDPOINT, {
        session_id: SID,
        query_text: p.query,
        locale: LOCALE || undefined,
        query_language: guessQueryLanguage(p.query),
        results_shown: p.results,
        results_count: p.count,
        source: 'dropdown' // TASK-2902 — dropdown is authoritative; a later page-0 must not overwrite this count
      });
    }, DEBOUNCE_MS);
  }

  (function wrapFetch() {
    try {
      var orig = window.fetch;
      if (typeof orig !== 'function') return;
      window.fetch = function (input, init) {
        var url = '';
        try { url = (typeof input === 'string') ? input : (input && input.url) || ''; } catch (e) {}
        var isPredictive = url && url.indexOf('section_id=predictive-search') !== -1;
        var promise = orig.apply(this, arguments);
        if (isPredictive) {
          try {
            var query = qParam(url);
            if (query) {
              promise.then(function (resp) {
                try {
                  resp.clone().text().then(function (html) {
                    try {
                      var doc = new DOMParser().parseFromString(html, 'text/html');
                      schedulePredictiveLog(query, parsePredictiveResults(doc), predictiveTotalCount(doc));
                    } catch (e) {}
                  }).catch(function () {});
                } catch (e) {}
              }).catch(function () {});
            }
          } catch (e) {}
        }
        return promise;
      };
    } catch (e) {}
  })();

  // ===================================================================
  // 2) Full /search results page — log one /query for the committed search
  // ===================================================================
  function currentSearchTerm() {
    try {
      var params = new URLSearchParams(window.location.search);
      return (params.get('q') || '').trim();
    } catch (e) { return ''; }
  }

  function isSearchResultsPage() {
    try {
      var hasQ = !!currentSearchTerm();
      var isSearch = /\/search(\/|$|\?)/.test(window.location.pathname + '?') ||
                     !!document.querySelector('.template-search');
      return hasQ && isSearch && !!document.getElementById('product-grid');
    } catch (e) { return false; }
  }

  // TASK-2902: the live /search page is the custom-search section, whose grid is
  // <ul id="product-grid" class="ils-pcl-grid"><li class="ils-pcl-grid__item">…
  // with the product name in <a class="ils-pcl__name">. The old stock-Dawn
  // selectors (#product-grid .grid__item / .card__heading) matched NOTHING there,
  // so parsePageResults() always returned [] and the page path logged
  // results_count=0 no matter how many products were shown. Prefer the real
  // custom-search selectors; keep the Dawn selectors as a fallback in case a
  // stock-Dawn page/theme is ever rendered.
  function collectResultItems() {
    var items = document.querySelectorAll('#product-grid > li.ils-pcl-grid__item');
    if (!items || !items.length) items = document.querySelectorAll('#product-grid .grid__item');
    return items;
  }

  function itemHeading(item) {
    return item.querySelector('.ils-pcl__name') ||
           item.querySelector('.card__heading a, .card__information .card__heading a, .card__heading');
  }

  function parsePageResults() {
    var out = [];
    try {
      var items = collectResultItems();
      for (var i = 0; i < items.length && out.length < MAX_RESULTS; i++) {
        var a = items[i].querySelector('a[href*="/products/"]') ||
                items[i].querySelector('a[href]');
        var heading = itemHeading(items[i]);
        var title = textOf(heading) || textOf(a);
        var handle = handleFromHref(a && a.getAttribute('href'));
        if (handle || title) out.push({ position: i + 1, handle: handle, title: title });
      }
    } catch (e) {}
    return out;
  }

  // TASK-2902: prefer the TRUE total (search.results_count, exposed as
  // data-total-results on #product-grid in custom-search.liquid) over the on-page
  // item count — the grid only renders one page (products_per_page), so counting
  // items would undercount a multi-page result set. Falls back to the parsed
  // item count when the attribute is absent (e.g. a stock-Dawn page).
  function pageTotalCount() {
    try {
      var grid = document.getElementById('product-grid');
      if (grid && grid.hasAttribute('data-total-results')) {
        var n = parseInt(grid.getAttribute('data-total-results'), 10);
        if (isFinite(n) && n >= 0) return n;
      }
    } catch (e) {}
    return undefined;
  }

  function logSearchPage() {
    try {
      if (!isSearchResultsPage()) return;
      var term = currentSearchTerm();
      if (!term) return;
      var results = parsePageResults();
      var total = pageTotalCount();
      post(QUERY_ENDPOINT, {
        session_id: SID,
        query_text: term,
        locale: LOCALE || undefined,
        query_language: guessQueryLanguage(term),
        results_shown: results,
        results_count: (total === undefined ? results.length : total),
        source: 'page' // TASK-2902 — lets the backend keep a good dropdown count from being clobbered by a page-0 race
      });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', logSearchPage, { once: true });
  } else {
    logSearchPage();
  }

  // ===================================================================
  // 3) Click on a result (dropdown OR results page) → /click
  // ===================================================================
  function indexAmong(nodeList, target) {
    for (var i = 0; i < nodeList.length; i++) {
      if (nodeList[i] === target || nodeList[i].contains(target)) return i;
    }
    return -1;
  }

  function onDocClick(ev) {
    try {
      var t = ev.target;
      if (!t || !t.closest) return;
      var anchor = t.closest('a[href]');
      if (!anchor) return;

      // --- predictive dropdown result ---
      var predRoot = t.closest('predictive-search');
      if (predRoot && predRoot.querySelector('#predictive-search-results')) {
        var opts = predRoot.querySelectorAll('#predictive-search-results li[role="option"]');
        var li = anchor.closest('li[role="option"]');
        var pos = li ? indexAmong(opts, li) : -1;
        var input = predRoot.querySelector('input[type="search"], input[name="q"]');
        var heading = anchor.querySelector('.predictive-search__item-heading') ||
                      (li && li.querySelector('.predictive-search__item-heading'));
        post(CLICK_ENDPOINT, {
          session_id: SID,
          clicked_position: pos >= 0 ? pos + 1 : undefined,
          clicked_target: {
            handle: handleFromHref(anchor.getAttribute('href')),
            title: textOf(heading)
          },
          query_text: input && input.value ? input.value.trim() : undefined
        });
        return;
      }

      // --- full results-page product card ---
      // TASK-2902: custom-search grid uses li.ils-pcl-grid__item / .ils-pcl__name;
      // fall back to stock-Dawn .grid__item / .card__heading.
      var grid = document.getElementById('product-grid');
      if (grid && grid.contains(anchor)) {
        var cards = collectResultItems();
        var card = anchor.closest('li.ils-pcl-grid__item') || anchor.closest('.grid__item');
        var gpos = card ? indexAmong(cards, card) : -1;
        var cardHeading = card && itemHeading(card);
        post(CLICK_ENDPOINT, {
          session_id: SID,
          clicked_position: gpos >= 0 ? gpos + 1 : undefined,
          clicked_target: {
            handle: handleFromHref(anchor.getAttribute('href')),
            title: textOf(cardHeading) || textOf(anchor)
          },
          query_text: currentSearchTerm() || undefined
        });
      }
    } catch (e) {}
  }

  try {
    document.addEventListener('click', onDocClick, true); // capture: fires before navigation
  } catch (e) {}
})();
